import React, { useRef } from 'react';
import { X, CheckCircle2, GripVertical } from 'lucide-react';
import { getRegionLabel } from './constants';

// Matches the panel's min-w-[220px] below. Used to decide whether the absolute
// position would push the panel outside the available element width.
const PANEL_MIN_WIDTH = 220;

export default function SelectedRegionsPanel({
  selectedSlugs,
  onRemove,
  disabled = false,
  editMode = false,
  panelPosition,
  onPanelPositionChange,
  containerWidth = null,
}) {
  const slugs = Array.from(selectedSlugs);
  const panelRef = useRef(null);
  const dragStateRef = useRef(null);

  const hasPosition =
    panelPosition && typeof panelPosition.x === 'number' && typeof panelPosition.y === 'number';

  // Only honor the absolute position when the element is actually wide enough to
  // hold the panel at that x. When it isn't, absolute positioning would render
  // the panel outside the element box where overflow-hidden (builder) or the
  // canvas edge (preview) clips it — appearing not to render at all. Falling back
  // to inline lets the flex-wrap container stack it below the figures instead, so
  // it always stays visible. Edit-layout dragging still forces absolute.
  const fitsPositioned =
    hasPosition &&
    (editMode ||
      containerWidth == null ||
      panelPosition.x + PANEL_MIN_WIDTH <= containerWidth);
  const isPositioned = fitsPositioned;

  const handlePointerDown = (e) => {
    if (!editMode || !onPanelPositionChange) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);

    let startX = panelPosition?.x;
    let startY = panelPosition?.y;
    if (startX === undefined || startY === undefined) {
      const panel = panelRef.current;
      startX = panel?.offsetLeft ?? 0;
      startY = panel?.offsetTop ?? 0;
    }
    dragStateRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX,
      startY,
    };
  };

  const handlePointerMove = (e) => {
    const drag = dragStateRef.current;
    if (!drag) return;
    onPanelPositionChange?.({
      x: drag.startX + (e.clientX - drag.startClientX),
      y: drag.startY + (e.clientY - drag.startClientY),
    });
  };

  const handlePointerUp = (e) => {
    if (!dragStateRef.current) return;
    e.currentTarget?.releasePointerCapture?.(e.pointerId);
    dragStateRef.current = null;
  };

  const positionStyle = isPositioned
    ? { position: 'absolute', left: panelPosition.x, top: panelPosition.y, zIndex: 5 }
    : {};

  return (
    <div
      ref={panelRef}
      className={`border rounded-lg bg-white p-4 min-w-[220px] ${editMode ? 'ring-2 ring-blue-300' : ''}`}
      style={positionStyle}
    >
      <div
        className={`flex items-center gap-2 mb-3 pb-2 border-b ${editMode ? 'cursor-move select-none' : ''}`}
        onPointerDown={editMode ? handlePointerDown : undefined}
        onPointerMove={editMode ? handlePointerMove : undefined}
        onPointerUp={editMode ? handlePointerUp : undefined}
        onPointerCancel={editMode ? handlePointerUp : undefined}
        style={editMode ? { touchAction: 'none' } : undefined}
      >
        <CheckCircle2 className="text-teal-600 w-5 h-5" />
        <span className="font-semibold text-gray-800">Selected Areas</span>
        {editMode && <GripVertical className="w-4 h-4 text-gray-400 ml-auto" />}
      </div>

      {slugs.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          No areas selected yet. Click the body map to select.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {slugs.map((slug) => (
            <li
              key={slug}
              className="flex items-center justify-between bg-red-50 text-red-700 text-sm rounded px-3 py-2"
            >
              <span>{getRegionLabel(slug)}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onRemove(slug)}
                  aria-label={`Remove ${getRegionLabel(slug)}`}
                  className="text-red-500 hover:text-red-700 ml-2"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
